import { BillingType } from '~constants/maps';

import { SahCategory, SahClaimStatus, StatementStatus, Supplement } from '~lookups/models';

import { ServiceTypeRow } from '~appointments/reducers/serviceTypes';
import { ClientRow } from '~main/reducers/clients/clients';

export interface ClientService {
  id: string;
  company_id?: number;
  client_id?: number;
  service_id?: string;
  start_date?: string;
  end_date?: string;
  meta_information?: any;
}

export interface ClientContribution {
  id: string;
  company_id?: number;
  client_id?: number;
  category_id?: string;
  contrib_proportion?: number;
  start_date?: string;
  end_date?: string;
  meta_information?: any;
}

export interface Classification {
  id: number;
  code: string;
  name: string;
  meta_information?: any;
  combined_name?: string;
}

export interface ClientClassification {
  id: string;
  company_id?: number;
  client_id?: number;
  classification_id?: string;
  classification_type?: string;
  start_date?: string;
  end_date?: string;
  meta_information?: any;
}

export interface SahBudget {
  id: string;
  company_id?: number;
  client_id?: number;
  sah_budget_id?: string;
  start_date?: string;
  end_date?: string;
  total_amount?: number;
  available_amount?: number;
  used_amount?: number;
  origin_tag?: string;
  is_archived?: boolean;
  meta_information?: any;
}

interface SahClients {
  id: string | number;
  first_name: string;
  surname: string;
}

export interface SahClientServicesGrouped extends SahClients {
  client_id: number;
  services_count: number;
}

export interface SahClientContributionsGrouped extends SahClients {
  contributions_count: number;
}

export interface SupplierInvoiceItem {
  id: string;
  invoice_id?: string;
  client_budget_id?: string;
  client_package_id?: number;
  service_type_id?: number;
  service_date?: string;
  purchase_order_number?: string;
  description?: string;
  notes?: string;
  units?: number;
  unit_amount?: number;
  gst_type?: 'free' | 'in' | 'nin';
  account_code_id?: string;
  sah_item_id?: string;
  sah_wrap_id?: string;
  is_first_payment_item?: boolean | null;
  [key: string]: any;
}

export interface ClientHcpUnspentFunds {
  funding_type_name: string | null;
  total_amount: number | null;
  available_amount: number | null;
  used_amount: number | null;
  updated_at: string;
}

// budgets
export interface Budget {
  id: string;
  reference: string;
  client_id: number;
  client_sah_budget_id: string;
  client_package_id: number;
  price_plan_id: string;
  price_plan_revision_id: string;
  package_fixed_id: number;
  status_id: string;
  status?: ClientBudgetStatus;
  start_date: string;
  end_date: string;
  total_amount: number;
  available_amount?: number;
  total_allocated?: number;
  total_includes_case_management?: boolean;
  sections: Section[];
  price_plan_revision: PricePlanRevision;
  sah_budget: SahBudget;
  created_at: string;
  client: ClientRow;
  grand_total: SectionTotal;
  package_fixed: Partial<ClientPackage>;
  supplements?: Supplement[];
  ignore_sah_filters?: boolean;
}

export interface ClientBudgetStatus {
  id: string;
  name: string;
  tag?: string;
}

export interface Section {
  id: string;
  category_id: string;
  category_name: string;
  contrib_proportion: number | null;
  items: SectionItem[];
  totals: SectionTotal;
  service_totals?: ServiceTotal[];
  category?: SahCategory;
}

export const COPY_STATUS = {
  CARRIED_OVER: 'carried_over',
  PRICE_CHANGED: 'price_changed',
  REMOVED: 'removed',
  NEW: 'new',
} as const;

export type CopyStatus = (typeof COPY_STATUS)[keyof typeof COPY_STATUS];

export interface SectionItem {
  id: string;
  frequency_id: string;
  section_id?: string;
  shift_id: string;
  service_type_id: string;
  start_date: string;
  end_date: string;
  week_days?: number[];
  quantity?: number;
  unit_type?: string;
  unit_amount?: number;
  unit_amount_ph?: number;
  total_amount?: number;
  include_public_holidays: boolean;
  is_external_provider: boolean;
  external_reference?: string;
  generate_provider_orders?: boolean;
  category_id?: string;
  service_type?: Partial<ServiceTypeRow>;
  week_days_text?: string;
  shift?: {
    id: string;
    name: string;
  };
  frequency?: {
    id: string;
    name: string;
  };
  billing_type?: BillingType;
  copy_status?: CopyStatus;
  previous_unit_amount?: number;
}

export interface ServiceTotal {
  service_type_id: string;
  total: number;
  contribution: number;
  govSubsidy: number;
}

export interface ExternalProviderData {
  is_external_provider: boolean;
  external_reference?: string;
}

export interface SectionTotal {
  cost: number;
  contribution: number;
  govSubsidy: number;
  case_management: number;
}

export interface BudgetActivitiesLog {
  external_reference_user_name: string;
  feature_tag: string;
  type_tag: string;
  created_at: string;
  changes: {
    budget: Detail[];
    sections: SectionChange[];
    items: ItemChange[];
  };
}

export interface SectionChange {
  section_id: string;
  category: SahCategory;
  details: Detail[];
}

export interface ItemChange {
  section_id: string;
  item_id?: string;
  service_type_id: number;
  details: Detail[];
}

export interface Detail {
  field: string;
  from?: string;
  to: string;
}

// price-plans
export interface PricePlan {
  id: string;
  company_id: number;
  name: string;
  start_date: string;
  end_date: string;
  is_deleted: boolean;
  revisions?: PricePlanRevision[];
}

export type PricePlanRow = Omit<PricePlan, 'revisions'> & {
  latest_revision: PricePlanRevision;
};

export type PricePlanModalRow = {
  id: string;
  category_id?: string;
  service_type_id: number;
  billing_type?: BillingType;
  [shiftId: string]: number | string | Record<string, PricePlanService> | ServiceTypeRow | undefined;
};

// price-plan-revisions
export interface PricePlanRevision {
  id: string;
  plan_id: string;
  sequence: number;
  services: PricePlanService[];
  billing_rules: PricePlanBillingRule[];
  price_plan?: PricePlan;
  is_latest_revision?: boolean;
}

export interface PricePlanService {
  id: string;
  revision_id: string;
  service_type_id: number;
  category_id: string;
  shift_id: string;
  cost: number;
  service_type: ServiceTypeRow;
  service_type_assoc?: {
    sah_service: {
      category: {
        id: string;
        name: string;
        tag: string;
      };
    };
  };
}

export interface PricePlanBillingRule {
  id: string;
  billing_type: BillingType;
  service_type_id: number;
  category_id: string;
  service_type_assoc?: {
    sah_service: {
      category: {
        id: string;
        name: string;
        tag: string;
      };
    };
  };
}

export interface BudgetType {
  id: string;
  sah_reference_id: string;
  name: string;
  tag: string;
}

export interface SupplierInvoice {
  id: string;
  client_id: number;
  supplier_id: string;
  created_at: string;
  reference: string;
  due_date: string;
  invoice_date: string;
  status_id: string;
  status?: SupplierInvoiceStatus;
  items?: SupplierInvoiceItem[];
  file?: InvoiceFile;
  files?: InvoiceFile[];
  net_total?: number;
  gross_total?: number;
}

export interface InvoiceFile {
  id: string;
  invoice_id: string;
  file_name: string;
  file_type: string; // e.g., "image/jpeg", "application/pdf"
  file_path: string;
  data?: string | null; // Object URL (e.g., "blob:http://localhost:3000/abc123...")
}
export interface Supplier {
  id: string;
  company_id: number;
  name: string;
  account_reference: string;
}

export interface SupplierInvoiceStatus {
  id: string;
  name: string;
  tag: string;
  to_statuses: [{ from_status_id: string }];
  from_statuses: [{ to_status_id: string }];
  createdby_id: number;
  created_at: string;
}

export interface ClientWithBudget {
  id: number;
  first_name: string;
  surname: string;
  full_name: string;
  budgets: Array<Budget>;
  latest_budget?: Budget | null;
}

export interface ClientPackage {
  id: number;
  name: string;
  company_id: number;
  funding_type_id: number;
  funding_type: FundingType;
}

export interface FundingType {
  id: number;
  name: string;
  sequence: number;
  company_id: number;
  is_deleted?: boolean;
  sah_code?: string;
}

// Models for Budget Tracker
export interface Tracker {
  client: { id: number; full_name: string };
  start: string;
  end: string;
  status: 'draft' | 'active' | 'expired' | 'closed';
  total_amount: number;
  summary: TrackerData;
  categories: TrackerCategory[];
  services: TrackerService[];
}

export interface TrackerData {
  allocated: number;
  booked: number;
  claimed: number;
  remaining: number;
  overspend: number;
  utilization: number;
  units: 'currency' | 'hours';
}

export interface TrackerCategory {
  id: string;
  name: string;
  code: string;
  data: TrackerData;
}

export interface TrackerService {
  id: string;
  category_id: string;
  name: string;
  code: string;
  data: TrackerData;
}

// Claims
export interface Claim {
  id: string;
  reference: string;
  status: SahClaimStatus;
  company_id: number;
  sah_reference_id: string;
  start_date: string;
  end_date: string;
  is_validating: boolean;
  validated_at?: string;
  items?: ClaimItem[];
  items_count?: number;
  items_valid?: number;
  items_with_errors?: number;
  items_ignored?: number;
}

export interface ClaimItem {
  id: string;
  claim: Claim;
  client_id: number;
  client: { id: number; full_name: string };
  service_date: string;
  sah_service_id?: string;
  sah_wrap_id?: string;
  sah_item_id?: string;
  sah_detail_description?: string;
  sah_hpt_id?: string;
  sah_hpt_description?: string;
  is_first_payment_item?: boolean | null;
  client_budget_id?: string;
  origin_tag?: string;
  origin_appointment_id?: number;
  origin_supplier_invoice_item_id?: string;
  units?: number;
  unit_amount?: number;
  is_ignored?: boolean;
  is_evidence_required?: boolean;
  reconciliation_item?: ReconItem;
  statement?: Statement;
  alert_items?: ClaimItemAlert[];
}

export interface ClaimItemAlert {
  id: string;
  claim_item_id: string;
  alert_id: string;
  alert: {
    id: string;
    reference_id: string;
    message: string;
    alert_level: 'error' | 'warning';
  };
}

export interface ClaimItemFile {
  id: string;
  claim_item: ClaimItem;
  file_name: string;
  file_type: string;
  file_path: string;
}

// Reconciliations
export interface Reconciliation {
  id: string;
  company_id: number;
  sah_reference_id: string;
  payment_date: string;
  sah_document: any;
  summary: ReconSummary;
  breakdown: ReconClient[];
  items: ReconItem[];
}

export interface ReconItem {
  id: string;
  reconciliation_id: string;
  funding_source_id: number;
  sah_invoice_id: string;
  sah_invoice_item_id: string;
  contribution_amount: number;
  final_payment_amount: number;
  meta_information?: any;
  claim_item?: ClaimItem;
}

export interface ReconSummary {
  total_paid?: number;
  total_gov?: number;
  total_co_contrib?: number;
  total_records?: number;
}

export interface ReconAmendmentItem {
  id: string;
  service: string;
  date: string;
  originalCost: number;
  newCost: number;
  govPaysChange: number;
  youPayChange: number;
  meta_budgets?: any[];
  funding_sources?: any[];
}

export interface ReconClient {
  client_id: number;
  full_name: string;
  items: ReconClientItem[];
  currentItems?: ReconClientItem[];
  amendmentGroups?: ReconAmendmentItem[];
  adjustmentGroups?: ReconAmendmentItem[];
  total_claimed: number;
  total_gov_contribution_amount: number;
  total_co_contribution_amount: number;
  total_paid: number;
  status: 'approved' | 'adjusted' | 'rejected';
  services_count: number;
}

export interface ReconClientItem {
  id: string;
  funding_source_ids?: number[];
  funding_sources?: any[];
  service_name: string;
  service_date: string;
  claimed: number;
  paid: number;
  gov_contribution_amount?: number;
  co_contribution_amount?: number;
  meta_budgets?: any[];
  reference?: string;
  sah_entitlement_type?: string;
  sah_invoice_item_id?: string;
}

// Statements
export interface Statement {
  id: string;
  status: StatementStatus;
  client_id: number;
  company_id: number;
  start_date: string;
  end_date: string;
}

// Client Invoices / Co-Contributions
export interface ClientInvoiceStatus {
  id: string;
  name: string;
  tag: string;
  is_active: boolean;
  colour: string;
  sequence: number;
}

export interface ClientInvoiceItem {
  id: string;
  invoice_id: string;
  service_date: string;
  description: string;
  units: number;
  unit_amount: number;
  created_at: string;
  createdby_id: number;
  updated_at: string;
  updatedby_id: number | null;
}

export interface ClientInvoice {
  id: string;
  status_id: string;
  client_id: number;
  company_id: number;
  invoice_date: string;
  reference: string | null;
  due_date: string;
  created_at: string;
  createdby_id: number;
  updated_at: string;
  updatedby_id: number | null;
  status: ClientInvoiceStatus;
  items: ClientInvoiceItem[];
  client: ClientInvoiceClient;
  total_amount: number;
}

interface ClientInvoiceClient extends ClientRow {
  send_to: string;
}
